// src/components/Admin/PostCommentManager.jsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    ToggleButtonGroup,
    ToggleButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';

import ArticleIcon from '@mui/icons-material/Article';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ForumIcon from '@mui/icons-material/Forum';
import DescriptionIcon from '@mui/icons-material/Description';
import HistoryIcon from '@mui/icons-material/History';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import RuleIcon from '@mui/icons-material/Rule';
import ChatIcon from '@mui/icons-material/Chat';
import BarChartIcon from '@mui/icons-material/BarChart';

// ───────── 게시글 ─────────
import CreatePostPointSetting from './PostManager/CreatePostPointSetting';
import PostsTable from './PostManager/PostsTable';
import PostsLogTable from './PostManager/PostsLogTable';

// ───────── 댓글 ─────────
import CreateCommentPointSetting from './CommentManager/CreateCommentPointSetting';
import CommentsTable from './CommentManager/CommentsTable';
import CommentsLogTable from './CommentManager/CommentsLogTable';

// ───────── 채팅 ─────────
import ChatLogPage from './ChatManager/Chat/ChatLogPage';
import UserMessageStats from './ChatManager/Chat/UserMessageStats';

import api from '../../api/axios';

export default function PostCommentManager() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // ≤600 px
    const [tab, setTab] = useState(0); // 1차 탭
    const [subTab, setSubTab] = useState(0); // 2차 탭
    const [comments, setComments] = useState([]);

    /* ───────── 댓글 목록 로드 ───────── */
    useEffect(() => {
        if (tab === 1 && subTab === 1) fetchComments(); // 댓글 목록 탭일 때만
    }, [tab, subTab]);

    const fetchComments = () =>
        api
            .get('/api/comments/admin/')
            .then((res) => setComments(res.data.comments || []))
            .catch(() => alert('댓글 목록을 불러오는 데 실패했습니다.'));

    /* ───────── 댓글 액션 ───────── */
    const handleSoftDelete = (id) =>
        api
            .post(`/api/comments/${id}/soft-delete/`)
            .then(() => {
                alert('댓글이 삭제되었습니다.');
                fetchComments();
            })
            .catch(() => alert('삭제 실패'));

    const handleRestore = (id) =>
        api
            .post(`/api/comments/${id}/restore/`)
            .then(() => {
                alert('댓글이 복구되었습니다.');
                fetchComments();
            })
            .catch(() => alert('복구 실패'));

    const handleHardDelete = (id) => {
        if (!window.confirm('정말 영구 삭제하시겠습니까?')) return;
        api
            .delete(`/api/comments/${id}/permanent-delete/`)
            .then(() => {
                alert('댓글이 완전히 삭제되었습니다.');
                fetchComments();
            })
            .catch(() => alert('삭제 실패'));
    };

    const handleToggleBlind = (id, isHidden) =>
        api
            .post(`/api/comments/${id}/blind/`, {
                is_hidden: isHidden,
                memo: isHidden ? '블라인드 처리' : '블라인드 해제',
            })
            .then(() => {
                alert(`댓글이 ${isHidden ? '블라인드 처리' : '해제'}되었습니다.`);
                fetchComments();
            })
            .catch(() => alert('처리에 실패했습니다.'));

    /* ───────── 모바일 Overscroll 방지 ───────── */
    useEffect(() => {
        if (!isMobile) return;
        const prev = document.body.style.overscrollBehaviorX;
        document.body.style.overscrollBehaviorX = 'none';
        return () => {
            document.body.style.overscrollBehaviorX = prev;
        };
    }, [isMobile]);

    /* ───────── 1차 탭 렌더 ───────── */
    const renderMainTabs = () => {
        // PC: Tabs(fullWidth)
        if (!isMobile) {
            return (
                <Tabs
                    value={tab}
                    onChange={(_, v) => {
                        setTab(v);
                        setSubTab(0);
                    }}
                    variant="fullWidth"
                    indicatorColor="primary"
                    textColor="primary"
                    sx={{
                        '& .MuiTab-root': {
                            minHeight: 48,
                            fontSize: '1rem',
                        },
                    }}
                >
                    <Tab icon={<ArticleIcon />} iconPosition="start" label="게시글" />
                    <Tab icon={<ChatBubbleOutlineIcon />} iconPosition="start" label="댓글" />
                    <Tab icon={<ForumIcon />} iconPosition="start" label="채팅" />
                </Tabs>
            );
        }

        // Mobile: ToggleButtonGroup(fullWidth)
        return (
            <ToggleButtonGroup
                exclusive
                fullWidth
                color="primary"
                value={tab}
                onChange={(_, v) => {
                    if (typeof v !== 'number') return; // 선택 해제 방지
                    setTab(v);
                    setSubTab(0);
                }}
                size="small"
                sx={{
                    py: 0.5,
                    '& .MuiToggleButton-root': {
                        flex: 1,
                        py: 0.8,
                        fontSize: '0.8rem',
                        gap: 0.5,
                    },
                }}
            >
                <ToggleButton value={0}>
                    <ArticleIcon sx={{ fontSize: '1.05rem' }} />
                    게시글
                </ToggleButton>
                <ToggleButton value={1}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: '1.05rem' }} />
                    댓글
                </ToggleButton>
                <ToggleButton value={2}>
                    <ForumIcon sx={{ fontSize: '1.05rem' }} />
                    채팅
                </ToggleButton>
            </ToggleButtonGroup>
        );
    };

    /* ───────── 2차 탭 렌더 ───────── */
    const renderSubTabs = () => {
        // PC: Tabs 유지
        if (!isMobile) {
            return (
                <Tabs
                    value={subTab}
                    onChange={(_, v) => setSubTab(v)}
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': {
                            minHeight: 48,
                            fontSize: '0.95rem',
                            px: 2,
                        },
                    }}
                >
                    {tab === 0 && [
                        <Tab key="post-0" icon={<RuleIcon />} iconPosition="start" label="글 포인트" />,
                        <Tab key="post-1" icon={<DescriptionIcon />} iconPosition="start" label="게시글 목록" />,
                        <Tab key="post-2" icon={<HistoryIcon />} iconPosition="start" label="게시글 로그" />,
                    ]}

                    {tab === 1 && [
                        <Tab key="comment-0" icon={<RuleIcon />} iconPosition="start" label="댓글 포인트" />,
                        <Tab key="comment-1" icon={<FormatListBulletedIcon />} iconPosition="start" label="댓글 목록" />,
                        <Tab key="comment-2" icon={<HistoryIcon />} iconPosition="start" label="댓글 로그" />,
                    ]}

                    {tab === 2 && [
                        <Tab key="chat-0" icon={<ChatIcon />} iconPosition="start" label="메시지 로그" />,
                        <Tab key="chat-1" icon={<BarChartIcon />} iconPosition="start" label="채팅 통계" />,
                    ]}
                </Tabs>
            );
        }

        // Mobile: ToggleButtonGroup 유지
        const buttons =
            tab === 0
                ? [
                    { v: 0, label: '글 포인트' },
                    { v: 1, label: '게시글' },
                    { v: 2, label: '로그' },
                ]
                : tab === 1
                    ? [
                        { v: 0, label: '댓글 포인트' },
                        { v: 1, label: '댓글' },
                        { v: 2, label: '로그' },
                    ]
                    : [
                        { v: 0, label: '메시지' },
                        { v: 1, label: '통계' },
                    ];

        return (
            <ToggleButtonGroup
                exclusive
                fullWidth
                color="primary"
                value={subTab}
                onChange={(_, v) => typeof v === 'number' && setSubTab(v)}
                size="small"
                sx={{ mb: 2, '& .MuiToggleButton-root': { flex: 1, py: 0.6, fontSize: '0.75rem' } }}
            >
                {buttons.map((b) => (
                    <ToggleButton key={b.v} value={b.v}>
                        {b.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        );
    };

    /* ───────── 렌더 ───────── */
    return (
        <Box
            sx={{
                p: isMobile ? 2 : 4,
                width: '100%',
                minWidth: 0,
                overflowX: 'hidden',
                overscrollBehaviorX: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}
        >
            {/* 타이틀 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ArticleIcon sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />
                <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold">
                    콘텐츠 관리
                </Typography>
            </Box>

            {/* ───── 1차 탭 ───── */}
            <Paper
                sx={{
                    mb: 1,
                    borderRadius: 2,
                    backgroundColor: '#f5f5f5',
                    px: isMobile ? 1 : 2,
                    overflowX: 'hidden',
                    minWidth: 0,
                }}
            >
                {renderMainTabs()}
            </Paper>

            {/* ───── 2차 탭 ───── */}
            {renderSubTabs()}

            {/* ───── 콘텐츠 영역 ───── */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: '60vh',
                    width: '100%',
                    minWidth: 0,

                    // ✅ 핵심: 모바일에서는 가로 스크롤/튕김 원천 차단
                    overflowX: isMobile ? 'hidden' : 'auto',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehaviorX: isMobile ? 'none' : 'contain',

                    '& > *': { minWidth: 0 },
                }}
            >
                {/* 게시글 */}
                {tab === 0 && subTab === 0 && <CreatePostPointSetting />}
                {tab === 0 && subTab === 1 && <PostsTable />}
                {tab === 0 && subTab === 2 && <PostsLogTable />}

                {/* 댓글 */}
                {tab === 1 && subTab === 0 && <CreateCommentPointSetting />}
                {tab === 1 && subTab === 1 && (
                    <CommentsTable
                        comments={comments}
                        onSoftDelete={handleSoftDelete}
                        onRestore={handleRestore}
                        onHardDelete={handleHardDelete}
                        onToggleBlind={handleToggleBlind}
                    />
                )}
                {tab === 1 && subTab === 2 && <CommentsLogTable />}

                {/* 채팅 */}
                {tab === 2 && subTab === 0 && <ChatLogPage />}
                {tab === 2 && subTab === 1 && <UserMessageStats />}
            </Box>
        </Box>
    );
}
